/**
 * Admin-portal staff/role permission model.
 *
 * The admin surfaces gate UI affordances on **permission**, not role
 * (e.g. `can("imports:run")`, never `role === "root"`). The mapping
 * role → permissions is encoded here as a const for FE-derived
 * enforcement until the backend ships permissions directly on
 * `/auth/me`.
 *
 * **Canonical 2-role model (SCRUM-131).** The platform role set is
 * `{root, support}` ONLY — sourced from `@artemis/contracts`
 * (`AuthMePlatformDto['role']`), never redefined locally (FE
 * non-negotiable #3). Backend gating, verbatim from the shipped
 * SCRUM-131 surface:
 *   - write actions (invite / suspend / reactivate / resend / revoke)
 *     are root-only (`PlatformRootGuard`);
 *   - list-read is root + support (`PlatformOnlyGuard`);
 *   - `POST /admin/users/:userId/reset-password` is root + support
 *     (`PlatformOnlyGuard`) — so `staff:reset_password` maps to BOTH
 *     roles, NOT the root-only write split.
 *
 * **Naming note re CLAUDE.md §3 (Frontend non-negotiable rule).**
 * That rule reserves the name `PermissionSlug` for the customer-user
 * permission union re-exported from `@artemis/contracts` (which
 * gates `<RequirePermission>`, `useHasPermission()`, `<IfPermission>`
 * — see `apps/admin/src/shared/auth/RequirePermission.tsx`). The
 * admin/staff permissions live in a disjoint namespace
 * (`companies:*`, `staff:*`, `imports:*`, `users:*`, `audit:*` —
 * colon-separated, distinct from contracts' dot-separated
 * `identity.users.read` style) and so the union is named
 * `AdminPermissionSlug` here. They coexist until a future ticket
 * merges them through `@artemis/contracts`.
 *
 * The gating component this ticket adds is `<IfAdminPermission>`
 * (separate file) — distinct from the contracts-based
 * `<RequirePermission>`.
 */

import type { components } from '@artemis/contracts';

/**
 * Canonical platform role union, DERIVED from the generated
 * `@artemis/contracts` DTO enum — never redefined locally
 * (FE non-negotiable #3). Resolves to `'support' | 'root'`.
 */
export type PlatformRole = components['schemas']['AuthMePlatformDto']['role'];

export const PLATFORM_ROLES = [
  'root',
  'support',
] as const satisfies readonly PlatformRole[];

/**
 * Compile-time exhaustiveness guard: fails to typecheck if the
 * contract union ever gains a member not listed in `PLATFORM_ROLES`.
 * Resolves to `true` only when the runtime array covers the full
 * union; any uncovered member makes the type `never` and the
 * assignment below errors.
 */
type _Exhaustive = Exclude<
  PlatformRole,
  (typeof PLATFORM_ROLES)[number]
> extends never
  ? true
  : never;
const _exhaustivenessCheck: _Exhaustive = true;
void _exhaustivenessCheck;

export function isPlatformRole(value: unknown): value is PlatformRole {
  return (
    typeof value === 'string' &&
    (PLATFORM_ROLES as readonly string[]).includes(value)
  );
}

/**
 * Admin-portal permission slugs — the colon-separated namespace that
 * the admin surfaces (Companies / Staff / Imports / Audit) gate on.
 * Disjoint from the dot-separated `identity.*` namespace owned by
 * `@artemis/contracts`. See file-level JSDoc for the naming
 * rationale.
 */
export type AdminPermissionSlug =
  // Companies
  | 'companies:read'
  | 'companies:create'
  | 'companies:transition_status'
  | 'companies:edit'
  // PMS-customer users (admin-side view; not the customer-facing
  // identity.users.* slugs from @artemis/contracts)
  | 'users:read'
  | 'users:invite'
  | 'users:impersonate'
  // Staff (platform employees)
  | 'staff:read'
  | 'staff:invite'
  | 'staff:edit_role'
  | 'staff:suspend'
  | 'staff:reset_password'
  | 'staff:reset_2fa'
  | 'staff:sign_out_all'
  | 'staff:manage_invitations'
  // Imports
  | 'imports:read'
  | 'imports:run'
  // Audit
  | 'audit:read';

const ADMIN_PERMISSION_SLUGS: ReadonlyArray<AdminPermissionSlug> = [
  'companies:read',
  'companies:create',
  'companies:transition_status',
  'companies:edit',
  'users:read',
  'users:invite',
  'users:impersonate',
  'staff:read',
  'staff:invite',
  'staff:edit_role',
  'staff:suspend',
  'staff:reset_password',
  'staff:reset_2fa',
  'staff:sign_out_all',
  'staff:manage_invitations',
  'imports:read',
  'imports:run',
  'audit:read',
];

export function isAdminPermissionSlug(
  value: unknown,
): value is AdminPermissionSlug {
  return (
    typeof value === 'string' &&
    (ADMIN_PERMISSION_SLUGS as readonly string[]).includes(value)
  );
}

/**
 * Role → permissions mapping, derived from the SCRUM-131 backend
 * guard surface (NOT a naive read/write split — codex BLOCKING-1):
 *
 *   - `root` holds every slug.
 *   - `support` holds every `*:read` slug PLUS the platform recovery
 *     actions guarded by `PlatformOnlyGuard` (root + support):
 *     `staff:reset_password`, `staff:reset_2fa`, `staff:sign_out_all`.
 *     It holds NO invite / suspend / edit / transition / run /
 *     impersonate slug, and NOT `staff:manage_invitations`
 *     (resend/revoke invitations are `PlatformRootGuard` = root-only).
 *
 * When the backend ships permissions directly on `/auth/me`, this
 * table retires in favor of the BE-emitted list.
 */
export const PERMISSIONS_BY_ROLE: Record<
  PlatformRole,
  ReadonlyArray<AdminPermissionSlug>
> = {
  root: ADMIN_PERMISSION_SLUGS,
  support: [
    'companies:read',
    'users:read',
    'staff:read',
    'imports:read',
    'audit:read',
    'staff:reset_password',
    // Platform recovery actions — `PlatformOnlyGuard` (root + support) on
    // the backend. `staff:manage_invitations` is deliberately ABSENT:
    // resend/revoke invitations are `PlatformRootGuard` (root-only).
    'staff:reset_2fa',
    'staff:sign_out_all',
  ],
};

/**
 * Does a given role hold a given permission per the
 * `PERMISSIONS_BY_ROLE` table? Pure function — no hook plumbing, no
 * React. Easy to unit-test and easy to call from any gate (route
 * guard, component gate, test helper).
 */
export function rolePermits(
  role: PlatformRole,
  permission: AdminPermissionSlug,
): boolean {
  return (PERMISSIONS_BY_ROLE[role] ?? []).includes(permission);
}
